import React, { useState, useEffect, useCallback } from "react"
import {
  Form,
  FormTextField,
  FormButton,
  FormAutocomplete,
  useWatch,
  FormSelectOption,
  useFormContext
} from "@carrotfertility/carotene-core-x"
import { Box, LabelWithTooltip, Link, Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import {
  renderAutocompleteProviderOption,
  useDebouncedSearch
} from "#/pages/provider-finder/components/keyword-search/keywordSearchUtils"
import { RecommendDoulaStatus } from "#/pages/provider-finder/recommend-doula/RecommendDoulaStatus"
import { Link as RouterLink } from "react-router-dom"
import useBenefit from "#/components/dynamic-content/hooks/useGetBenefit"
import { useProviderFinderClient } from "#/pages/provider-finder/hooks/useProviderFinderClient"
import buildCarrotApiRetryPolicy from "#/utils/CarrotApiRetryPolicy"
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from "@tanstack/react-query"
import ResponseError from "#/types/responseError"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"
import { MemberActions } from "#/services/memberActionTracking/memberActions"
import { CountryCode } from "#/utils/CountryCode"
import { normalizeSearchString } from "#/pages/provider-finder/utils/string"

interface FormValues {
  name: FormSelectOption
  email: string
  doula: TypeaheadDoula
}

export type TypeaheadDoula = {
  uuid: number
  name: string
  email: string
  primaryEmail: string
  eligibilityStatus: string
  location: { lat: number; lng: number }
  showInMemberApp: boolean
}

const TYPEAHEAD_DOULAS_QUERY_KEY = "GetTypeaheadDoulas"

function useTypeaheadDoulas(
  doulaNameSubstring: string,
  countryCode: CountryCode
): UseQueryResult<TypeaheadDoula[], ResponseError> {
  const client = useProviderFinderClient()

  return useQuery({
    queryKey: [TYPEAHEAD_DOULAS_QUERY_KEY, doulaNameSubstring],
    queryFn: () => {
      if (!doulaNameSubstring) {
        return null
      }

      return client.getTypeaheadDoulas(doulaNameSubstring, countryCode)
    },
    enabled: !!doulaNameSubstring,
    refetchOnWindowFocus: false,
    retry: buildCarrotApiRetryPolicy(3)
  })
}

export type AirtableDoula = {
  primaryEmail: string
  name: string
  countryCode: string
  coreOrPro: string
  state?: string
}

function useWriteDoulaToAirtable(): UseMutationResult<void, Error, AirtableDoula> {
  const client = useProviderFinderClient()
  return useMutation<void, Error, AirtableDoula>((payload: AirtableDoula) => client.writeDoulaToAirtable(payload), {
    retry: buildCarrotApiRetryPolicy(2)
  })
}

export function useRecommendDoulaSubmit() {
  const [selectedDoula, setSelectedDoula] = useState<TypeaheadDoula>(null)
  const [showStatus, setShowStatus] = useState<boolean>(false)
  const { mutate: addDoulaToAirTable } = useWriteDoulaToAirtable()
  const { mutate: addMemberAction } = useMemberActionTracking()
  const {
    company: { countryCode },
    state
  } = useCurrentUser()
  const benefit = useBenefit()

  function onSubmit(values: FormValues) {
    setSelectedDoula(values.doula)
    if (!values.doula) {
      const name = values.name.value as string
      addDoulaToAirTable(
        {
          primaryEmail: values.email.trim(),
          name: name.trim(),
          countryCode,
          coreOrPro: benefit?.data?.program.type,
          state: state
        },
        {
          onSuccess: () => {
            addMemberAction({
              memberActionTaken: MemberActions.DOULA_RECOMMENDATION_SUBITTED_SUCCESSFULLY
            })
          }
        }
      )
      setSelectedDoula(null)
    }
    setShowStatus(true)
  }

  return { onSubmit, selectedDoula, showStatus, setShowStatus, countryCode }
}

export function RecommendDoulaFormContainer({
  searchByNameInputValue = ""
}: {
  searchByNameInputValue?: string
}): JSX.Element {
  const { onSubmit, selectedDoula, showStatus, setShowStatus, countryCode } = useRecommendDoulaSubmit()

  return (
    <Form
      onSubmit={onSubmit}
      defaultValues={{
        email: "",
        name: {
          value: searchByNameInputValue || "",
          label: searchByNameInputValue || ""
        }
      }}
    >
      {showStatus ? (
        <RecommendDoulaStatus doula={selectedDoula} onReset={() => setShowStatus(false)} />
      ) : (
        <RecommendDoulaForm {...{ countryCode, searchByNameInputValue }} />
      )}
    </Form>
  )
}

function RecommendDoulaForm({
  countryCode,
  searchByNameInputValue
}: {
  countryCode: CountryCode
  searchByNameInputValue?: string
}): JSX.Element {
  const intl = useIntl()
  const theme = useTheme()
  const [openOptionsMenu, setOpenOptionsMenu] = useState<boolean>(false)
  const doulaNameValue: FormSelectOption = useWatch({ name: "name" })
  const emailValue: string = useWatch({ name: "email" })
  const { setValue, formState } = useFormContext()

  const {
    inputValue,
    loading: isTyping,
    setInputValue,
    searchValue
  } = useDebouncedSearch((value) => {
    setValue("name", { value, label: value }, { shouldDirty: true, shouldTouch: true })
  }, setOpenOptionsMenu)

  const typeAheadDoulasQuery = useTypeaheadDoulas(searchValue ?? searchByNameInputValue, countryCode)

  const isSearchLoading = isTyping || typeAheadDoulasQuery.isLoading
  const { data: benefitData, isLoading: isBenefitLoading } = useBenefit()

  const getDoulaFromSelectOption = useCallback(
    (option: FormSelectOption): TypeaheadDoula => {
      return (
        typeAheadDoulasQuery.data?.find((doula: TypeaheadDoula) => {
          const value = option?.value as string

          return normalizeSearchString(doula?.name) === normalizeSearchString(value)
        }) ?? null
      )
    },
    [typeAheadDoulasQuery.data]
  )

  useEffect(() => {
    const setValueOptions = { shouldDirty: true, shouldTouch: true }
    const doula = doulaNameValue ? getDoulaFromSelectOption(doulaNameValue) : null

    if (doula) {
      setValue("email", doula.primaryEmail, setValueOptions)
      setValue("doula", doula, setValueOptions)
    } else if (!doulaNameValue) {
      setValue("email", null, setValueOptions)
      setValue("doula", null, setValueOptions)
    } else {
      setValue("doula", null, setValueOptions)
    }
  }, [doulaNameValue, setValue, getDoulaFromSelectOption, typeAheadDoulasQuery.data])

  const submitButtonDisabled = !doulaNameValue?.value || !emailValue || formState.isSubmitting || isSearchLoading

  return (
    <>
      <Stack spacing={theme.tokens.spacing.xs} sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.lg })}>
        <Typography variant="h2">
          <FormattedMessage defaultMessage="Recommend a doula" />
        </Typography>
        <Typography>
          <FormattedMessage defaultMessage="We'll contact the doula for more information. Once we hear back, we’ll review their credentials within 10 business days if no additional information is needed. We’ll send you a message when our review process is complete." />
        </Typography>
      </Stack>
      {!isBenefitLoading && benefitData?.journeys?.pregnancy?.eligibleExpensesDetail?.doulaExpensesCovered ? (
        <Typography>
          <FormattedMessage
            defaultMessage="Learn more about our doula eligibility criteria in <link>your benefit guide</link>"
            values={{
              link: (linkContent) => (
                <Link color="inherit" component={RouterLink} to="/your-benefit-guide/coverage/pregnancy-and-postpartum">
                  {linkContent}
                </Link>
              )
            }}
          />
        </Typography>
      ) : null}
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.tokens.spacing.lg,
          marginBlockStart: theme.tokens.spacing.lg
        })}
      >
        <FormAutocomplete
          controllerProps={{ rules: { required: intl.formatMessage({ defaultMessage: "Required" }) } }}
          filterOptions={(x) => x}
          onInputChange={(_event, newInputValue, reason) => {
            if (reason !== "reset") {
              setInputValue(newInputValue)
            }
          }}
          name="name"
          label={intl.formatMessage({ defaultMessage: "Doula's name" })}
          options={
            isSearchLoading || typeAheadDoulasQuery.isError
              ? []
              : typeAheadDoulasQuery.data?.map((doula: TypeaheadDoula) => ({
                  value: doula.name,
                  label: doula.name
                }))
          }
          renderOption={(props, providerPrediction) => {
            const doula = getDoulaFromSelectOption(providerPrediction)
            return renderAutocompleteProviderOption(props, doula, inputValue)
          }}
          noOptionsText={<Typography>{intl.formatMessage({ defaultMessage: "No results found" })}</Typography>}
          open={openOptionsMenu}
          onClose={() => {
            setOpenOptionsMenu(false)
          }}
          loading={isSearchLoading}
          popupIcon={null}
          freeSolo
        />

        <FormTextField
          name="email"
          type="email"
          label={
            <LabelWithTooltip
              label={intl.formatMessage({ defaultMessage: "Doula's primary email" })}
              slotProps={{
                tooltip: {
                  placement: "bottom",
                  content: intl.formatMessage({
                    defaultMessage: "Please use the doula’s direct email, not a general agency address."
                  })
                },
                infoButton: {
                  "aria-label": "More info"
                }
              }}
            />
          }
          registerOptions={{
            required: intl.formatMessage({ defaultMessage: "Add the doula's email" }),
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: intl.formatMessage({
                defaultMessage: "Please enter a valid email address"
              })
            }
          }}
        />
        <FormTextField name="doula" sx={{ display: "none" }} aria-hidden="true" />
        <Box
          sx={{
            position: "absolute",
            insetBlockEnd: 16,
            insetInlineEnd: 16,
            display: "flex",
            justifyContent: "flex-end"
          }}
        >
          <FormButton type="submit" disabled={submitButtonDisabled}>
            <FormattedMessage defaultMessage="Submit" />
          </FormButton>
        </Box>
      </Box>
    </>
  )
}
